import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { apiFetchJson } from '../../apiClient';
import './Achievements.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

// eslint-disable-next-line no-unused-vars
const RARITY_ORDER = ['Common', 'Uncommon', 'Rare', 'Epic', 'Legendary'];

const CATEGORY_ALL = 'All';

function Achievements({ factFictionMode }) {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState(CATEGORY_ALL);

  const fetchAchievements = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetchJson(instance, '/api/gamification/achievements');
      setAchievements(unwrap(data));
    } catch (err) {
      setError(err.message || 'Failed to load achievements');
    } finally {
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => { fetchAchievements(); }, [fetchAchievements]);

  if (loading) return <div className="achievements-loading">{t('bookClub.achievements.loading')}</div>;
  if (error) return <div className="achievements-error">{error} <button onClick={fetchAchievements}>{t('bookClub.common.retry')}</button></div>;

  const categories = [CATEGORY_ALL, ...new Set(achievements.map(a => a.category).filter(Boolean))];
  const filtered = activeCategory === CATEGORY_ALL
    ? achievements
    : achievements.filter(a => a.category === activeCategory);

  const unlockedCount = achievements.filter(a => a.isUnlocked).length;

  return (
    <div className="achievements-container">
      <div className="achievements-summary">
        <span className="achievements-trophy-icon" role="img" aria-label="trophy">🏆</span>
        <span className="achievements-summary-text">
          {unlockedCount} / {achievements.length} {t('bookClub.achievements.unlocked')}
        </span>
      </div>

      {/* Category filter tabs */}
      {categories.length > 1 && (
        <div className="achievements-tabs">
          {categories.map(cat => (
            <button
              key={cat}
              className={`achievements-tab ${activeCategory === cat ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat === CATEGORY_ALL ? t('bookClub.achievements.all') : cat}
            </button>
          ))}
        </div>
      )}

      {/* Trophy grid */}
      <div className="achievements-grid">
        {filtered.map(a => (
          <AchievementCard key={a.id} achievement={a} />
        ))}
        {filtered.length === 0 && (
          <p className="achievements-empty">{t('bookClub.achievements.emptyCategory')}</p>
        )}
      </div>
    </div>
  );
}

function AchievementCard({ achievement }) {
  const { t } = useTranslation();
  const { name, description, rarity, isUnlocked, isHidden, unlockedAt, icon, percentOwned } = achievement;
  const rarityClass = (rarity || 'Common').toLowerCase();

  if (isHidden && !isUnlocked) {
    return (
      <div className={`achievements-card silhouette rarity-${rarityClass}`}>
        <div className="achievements-card-icon silhouette-icon">???</div>
        <div className="achievements-card-name">{t('bookClub.achievements.hidden')}</div>
        <div className="achievements-card-desc">{t('bookClub.achievements.hiddenDesc')}</div>
      </div>
    );
  }

  return (
    <div className={`achievements-card ${isUnlocked ? 'unlocked' : 'locked'} rarity-${rarityClass}`}>
      <div className="achievements-card-icon">{icon || '🏅'}</div>
      <div className="achievements-card-name">{name}</div>
      {description && <div className="achievements-card-desc">{description}</div>}
      <div className="achievements-card-meta">
        <span className={`achievements-rarity-badge rarity-${rarityClass}`}>{rarity || 'Common'}</span>
        {isUnlocked && unlockedAt && (
          <span className="achievements-unlock-date">
            {new Date(unlockedAt).toLocaleDateString()}
          </span>
        )}
      </div>
      {percentOwned != null && (
        <div className="achievements-percent">{t('bookClub.achievements.rarity', { percent: percentOwned })}</div>
      )}
    </div>
  );
}

export default Achievements;
